import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Instagram,
  Sparkles,
  Briefcase,
  GraduationCap,
  Cpu,
  LineChart,
  BookOpen,
  Mic,
  Award,
  ShieldCheck,
  Landmark,
  Users,
  Building2,
  Wrench,
} from "lucide-react";
import { api, openBooking, openPartners } from "../lib/site";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton, OutlineButton, NewsletterForm } from "../components/site/Primitives";
import EditorialMarquee from "../components/site/EditorialMarquee";
import Testimonials from "../components/site/Testimonials";
import FaqAccordion from "../components/site/FaqAccordion";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_beauty-courses-pro/artifacts/xz4satb1_1.png";
const GIO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_beauty-courses-pro/artifacts/pmt8kuxq_NAIL%20.NEW%20WEBSITE.png";

const HERO_LINES = ["Build your career.", "Grow your business.", "Elevate self-care."];
const lineVariant = {
  hidden: { y: "112%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.05, ease: EASE, delay: 0.35 + i * 0.13 } }),
};

const WHO_WE_SERVE = [
  "Individuals seeking vocational training",
  "Small business owners",
  "Skilled trades professionals",
  "Beauty professionals",
  "Workforce development organizations",
  "Community organizations",
  "Educational institutions",
  "Government agencies",
  "Employers investing in workforce development",
];

const SERVICES = [
  {
    icon: Briefcase,
    title: "Workforce Development",
    items: ["Career readiness", "Job skills", "Professional development", "Industry certifications"],
  },
  {
    icon: GraduationCap,
    title: "Vocational Education",
    items: ["Nail Specialty Trainee Program", "Professional licensing", "Instructor training", "Continuing education"],
  },
  {
    icon: Cpu,
    title: "AI Integration",
    items: ["AI training", "Automation", "Digital tools", "Marketing systems", "Operational efficiency"],
  },
  {
    icon: LineChart,
    title: "Small Business Consulting",
    items: ["Business planning", "Branding", "Pricing", "Growth strategies", "Marketing", "Automation"],
  },
  {
    icon: BookOpen,
    title: "Curriculum & Program Design",
    items: ["Training development", "Workshop facilitation", "Instructional design", "Compliance training"],
  },
  {
    icon: Mic,
    title: "Public Speaking",
    items: ["Conferences", "Industry events", "Educational workshops", "Business seminars"],
  },
];

const CURRENT_PROGRAMS = [
  { title: "4 Days to Stateboard Success: 27HR Nail Trainee Course", tag: "Vocational Education", to: "/course" },
  { title: "NailBoss Blueprint AI Edition", tag: "AI Integration", to: "/blueprint" },
  { title: "Business Coaching", tag: "Small Business Consulting", to: "/blueprint" },
  { title: "Workforce Workshops", tag: "Workforce Development", to: "/partners" },
];

const WHY = [
  { icon: Award, label: "20+ Years of Industry Experience" },
  { icon: GraduationCap, label: "NYS Approved Instructor" },
  { icon: Briefcase, label: "Certified Workforce Trainer" },
  { icon: Cpu, label: "AI Business Consultant" },
  { icon: Users, label: "Curriculum Developer" },
  { icon: ShieldCheck, label: "Government Contract Ready" },
];

const PARTNER_AUDIENCE = [
  { icon: Landmark, label: "Government agencies" },
  { icon: BookOpen, label: "Educational institutions" },
  { icon: Users, label: "Workforce development orgs" },
  { icon: Building2, label: "Nonprofit organizations" },
  { icon: Briefcase, label: "Small business programs" },
  { icon: Wrench, label: "Industry associations" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-brand-cream pt-28">
      <div className="max-w-[1440px] mx-auto w-full px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div className="relative z-10">
          <h1 className="font-serif font-light text-brand-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            {HERO_LINES.map((line, i) => (
              <span className="line-mask" key={line}>
                <motion.span className="block" custom={i} variants={lineVariant} initial="hidden" animate="show">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}
            className="text-brand-ink/70 font-light text-lg mt-7 max-w-xl leading-relaxed"
          >
            Whether you&apos;re booking a luxury beauty service, launching a new career,
            growing your salon or small business, or partnering with us to deliver
            workforce training, Nervani Beauty provides the education, expertise, and
            innovative solutions to help you succeed.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <PrimaryButton as="link" to="/programs" data-testid="hero-programs-btn">Explore Training Programs <ArrowRight size={16} /></PrimaryButton>
            <GoldButton as="link" to="/partners" data-testid="hero-partners-btn">Partner With Us</GoldButton>
            <OutlineButton onClick={openBooking} data-testid="hero-book-btn">Book Services</OutlineButton>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1.1, ease: EASE }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] h-[420px] sm:h-[540px] lg:h-[640px]">
            <motion.img style={{ y: imgY, scale }} src={HERO_IMG} alt="Nervani Beauty training" className="w-full h-full object-cover" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
            className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-[0_20px_50px_rgba(23,17,15,0.12)] px-6 py-5 flex items-center gap-4"
          >
            <div className="h-11 w-11 rounded-full bg-brand-blush flex items-center justify-center text-brand-rose"><Sparkles size={20} /></div>
            <div>
              <p className="font-serif text-2xl text-brand-ink leading-none">20+ yrs</p>
              <p className="text-xs text-brand-ink/60 mt-1">Training & industry experience</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />

      {/* WHO WE SERVE */}
      <section data-testid="who-we-serve" className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20">
          <Reveal>
            <Overline className="mb-5">Who we serve</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              For the dreamers, the doers, and the people investing in them.
            </h2>
            <p className="text-brand-ink/70 font-light mt-6 max-w-md leading-relaxed">
              Whether you&apos;re chasing a new career, growing a business, or building
              programs that lift others up — Nervani is here with the education,
              expertise, and heart to help you go further.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="who-we-serve-list">
              {WHO_WE_SERVE.map((w) => (
                <li key={w} className="flex items-start gap-3 py-2 border-b border-brand-ink/10 text-brand-ink">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-gold flex-shrink-0" />
                  <span className="text-base font-light">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* SERVICES section removed — Programs section below covers tangible offerings; /services and /programs pages cover depth */}

      {/* CURRENT PROGRAMS */}
      <section data-testid="current-programs" className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <Overline className="mb-5">Featured programs</Overline>
              <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl">
                Current programs, ready to enroll.
              </h2>
            </div>
            <Link to="/programs" data-testid="programs-all-link" className="text-sm tracking-wide text-brand-ink border-b border-brand-gold pb-0.5 hover:text-brand-rose transition-colors">
              View all programs →
            </Link>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CURRENT_PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <Link to={p.to} data-testid={`program-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block h-full rounded-3xl bg-white border border-brand-ink/5 p-8 transition-transform duration-500 hover:-translate-y-1">
                  <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-brand-rose">{p.tag}</span>
                  <h3 className="font-serif text-2xl md:text-[26px] text-brand-ink tracking-tight mt-4 leading-tight">{p.title}</h3>
                  <span className="inline-flex items-center gap-1.5 mt-6 text-sm tracking-wide text-brand-ink border-b border-brand-gold pb-0.5 group-hover:text-brand-rose transition-colors">
                    Learn more <ArrowUpRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NERVANI */}
      <section data-testid="why-section" className="bg-brand-ink py-24 md:py-32 text-brand-cream">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">
          <Reveal>
            <Overline dark className="mb-5">Why Nervani</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Twenty years of experience. One mission.
            </h2>
            <p className="text-brand-cream/70 font-light mt-6 max-w-md leading-relaxed">
              I&apos;ve spent my career building the credentials, expertise, and heart
              to help people grow — as clients, students, entrepreneurs, and partners.
              Everything I&apos;ve earned is here to serve what you&apos;re building.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="grid sm:grid-cols-2 gap-4" data-testid="why-list">
              {WHY.map((w) => (
                <li key={w.label} className="flex items-start gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                  <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-brand-gold/15 text-brand-gold flex items-center justify-center">
                    <w.icon size={18} strokeWidth={1.7} />
                  </div>
                  <p className="text-brand-cream text-[15px] font-light leading-snug">{w.label}</p>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PARTNER WITH US */}
      <section data-testid="partner-with-us" className="relative overflow-hidden py-24 md:py-32 bg-gradient-to-br from-brand-blush via-brand-cream to-brand-pink/40">
        <div className="absolute top-10 right-0 h-72 w-72 rounded-full bg-brand-pink/40 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <Overline className="mb-5">Partnerships</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Beauty was the beginning. Partnerships are what&apos;s next.
            </h2>
            <p className="text-brand-ink/70 font-light mt-6 max-w-xl leading-relaxed">
              I&apos;m building Nervani into a home for education, workforce training,
              and business consulting — grounded in beauty, expanding into every industry
              that trains its people. If you lead an organization ready to invest in
              growth, I&apos;d love to build with you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryButton onClick={openPartners} data-testid="partner-cta-btn">Partner With Us <ArrowRight size={16} /></PrimaryButton>
              <GoldButton as="link" to="/partners" data-testid="partner-view-btn">View Capabilities</GoldButton>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white/70 backdrop-blur border border-brand-ink/10 p-8 md:p-10">
              <p className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-rose mb-5">We partner with</p>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-4" data-testid="partner-audience-list">
                {PARTNER_AUDIENCE.map((p) => (
                  <li key={p.label} className="flex items-start gap-3 text-brand-ink">
                    <div className="h-9 w-9 rounded-xl bg-brand-blush flex items-center justify-center text-brand-rose flex-shrink-0">
                      <p.icon size={16} strokeWidth={1.7} />
                    </div>
                    <span className="text-sm font-light leading-tight pt-2">{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Meet the Founder */}
      <section data-testid="about-teaser" className="relative overflow-hidden py-16 md:py-24 bg-brand-cream">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-16 items-center">
          <Reveal className="relative flex justify-center lg:justify-start order-1">
            <div className="relative w-full max-w-xl">
              <div className="absolute inset-x-4 sm:inset-x-10 top-4 bottom-0 rounded-t-full rounded-b-[2.5rem] bg-gradient-to-b from-brand-pink to-brand-blush" />
              <div className="absolute inset-x-4 sm:inset-x-10 top-4 bottom-0 rounded-t-full rounded-b-[2.5rem] border border-brand-gold/40" />
              <span className="absolute z-20 top-6 right-6 sm:right-12 h-2.5 w-2.5 rounded-full bg-brand-gold" />
              <img
                src={GIO_IMG}
                alt="Giouvanna White, founder of Nervani Beauty"
                className="relative z-10 mx-auto h-[460px] sm:h-[600px] lg:h-[680px] w-auto object-contain object-bottom mix-blend-multiply"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-2 lg:pl-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-brand-gold" />
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-rose">The Founder</span>
            </div>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.0]">
              Meet Giouvanna White
            </h2>
            <p className="font-serif italic text-brand-gold text-xl sm:text-2xl md:text-[26px] mt-4 leading-snug">
              Educator · Consultant · Workforce Strategist
            </p>
            <div className="h-px w-16 bg-brand-gold/50 my-7" />
            <div className="space-y-5 text-brand-ink/75 font-light text-base md:text-lg leading-relaxed max-w-xl">
              <p>
                Founded by Giouvanna White, Nervani Beauty Boutique is a home for
                education, growth, and meaningful work. With 20+ years of industry
                experience at the helm, Nervani brings warmth, expertise, and heart
                to everything we build.
              </p>
              <p>
                From helping individuals earn their license to coaching entrepreneurs
                and partnering with organizations on workforce training — Nervani
                helps people build careers that last, starting in beauty and expanding
                into every industry that trains its people.
              </p>
            </div>
            <div className="mt-9">
              <PrimaryButton as="link" to="/about" data-testid="about-teaser-btn">
                Learn More About Giouvanna
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Featured products (kept — Shop is still a revenue line) */}
      <section data-testid="featured-products" className="bg-brand-cream pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <Overline className="mb-5">Featured products</Overline>
              <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight">Luxury self-care, curated.</h2>
            </div>
            <Link to="/shop" data-testid="featured-shop-all" className="text-sm tracking-wide text-brand-ink border-b border-brand-gold pb-0.5 hover:text-brand-rose transition-colors">Shop all products →</Link>
          </Reveal>
          <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1} className="group text-center flex flex-col items-center">
                <div className="relative w-full overflow-hidden rounded-3xl bg-brand-blush h-80 flex items-center justify-center">
                  <img src={p.image} alt={p.name} className="max-w-[85%] max-h-[85%] object-contain mix-blend-multiply transition-transform duration-[900ms] ease-out group-hover:scale-105 drop-shadow-[0_20px_40px_rgba(120,60,80,0.15)]" />
                  {p.badge && <span className="absolute top-4 left-4 bg-brand-ink text-brand-cream text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">{p.badge}</span>}
                </div>
                <h3 className="font-serif text-2xl text-brand-ink tracking-tight mt-6">{p.name}</h3>
                <span className="font-serif text-brand-rose text-xl mt-2">{p.price}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <FaqAccordion />

      {/* Newsletter + Instagram */}
      <section data-testid="home-newsletter" className="bg-brand-pink/50 py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <Overline className="mb-5">Stay in the know</Overline>
          <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight max-w-xl">Join the Nervani circle.</h2>
          <p className="text-brand-ink/70 font-light mt-5 max-w-lg">Class drops, business tips, product launches, and exclusive offers — straight to your inbox.</p>
          <div className="mt-9 w-full flex justify-center"><NewsletterForm testId="home-newsletter-form" source="home" /></div>
        </div>
      </section>

      <section data-testid="instagram-section" className="bg-brand-cream pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex justify-center">
          <a
            href="https://www.instagram.com/nervanibeauty/"
            target="_blank"
            rel="noreferrer"
            data-testid="home-instagram-link"
            className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-brand-ink/15 text-brand-ink hover:border-brand-gold hover:text-brand-gold transition-colors"
          >
            <Instagram size={20} />
            <span className="font-serif text-2xl">@nervanibeauty</span>
            <ArrowUpRight size={18} className="opacity-60 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>
      </section>
    </div>
  );
}
